/// <reference lib="webworker" />

import { CanvasRuntime, MediaImageRepositoryProcessing } from "./media_image_repository_processing";

export class OffscreenCanvasRuntime extends CanvasRuntime {
  create(width: number, height: number): OffscreenCanvas {
    return new OffscreenCanvas(width, height);
  }
  get2D(canvas: OffscreenCanvas): OffscreenCanvasRenderingContext2D {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Offscreen 2D context unavailable');
    return ctx as OffscreenCanvasRenderingContext2D;
  }
  async toBlobUrl(canvas: OffscreenCanvas, mime: string): Promise<string> {
    const blob = await canvas.convertToBlob({ type: mime });
    return URL.createObjectURL(blob);
  }
  async toDataURL(canvas: OffscreenCanvas, mime: string = 'image/png'): Promise<string> {
    const blob = await canvas.convertToBlob({ type: mime });
    const reader = new FileReader();
    return await new Promise<string>((resolve, reject) => {
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
  toHTMLCanvas(canvas: OffscreenCanvas): HTMLCanvasElement {
    const bitmap = canvas.transferToImageBitmap();
    const out = document.createElement('canvas');
    out.width = bitmap.width; out.height = bitmap.height;
    const ctx = out.getContext('bitmaprenderer') as ImageBitmapRenderingContext | null;
    if (ctx) {
      ctx.transferFromImageBitmap(bitmap);
    } else {
      const c2d = out.getContext('2d');
      if (!c2d) throw new Error('2D context unavailable to copy bitmap');
      c2d.drawImage(bitmap, 0, 0);
    }
    return out;
  }
}

export type ImageSource = Blob | ArrayBuffer | Response | string | URL;

export class MediaImageRepositoryProcessingOffscreen extends MediaImageRepositoryProcessing {
  constructor() { super(new OffscreenCanvasRuntime()); }

  // High-quality resize using drawImage (let browser choose best filter)
  async resize(image: HTMLImageElement, ratioX: number, ratioY: number, shouldPad: boolean = false): Promise<HTMLImageElement> {
    const targetW = Math.round(image.width * ratioX);
    const targetH = Math.round(image.height * ratioY);
    const maxEdge = shouldPad ? Math.max(targetW, targetH) : undefined;

    const can = this.runtime.create(shouldPad ? maxEdge! : targetW, shouldPad ? maxEdge! : targetH);
    const ctx = this.runtime.get2D(can);

    (ctx as any).imageSmoothingEnabled = true;
    (ctx as any).imageSmoothingQuality = 'high';

    if (shouldPad) {
      const cx = (maxEdge! - targetW) / 2;
      const cy = (maxEdge! - targetH) / 2;
      ctx.drawImage(image, 0, 0, image.width, image.height, cx, cy, targetW, targetH);
    } else {
      ctx.drawImage(image, 0, 0, image.width, image.height, 0, 0, targetW, targetH);
    }

    const html = this.runtime.toHTMLCanvas(can);
    const out = await this.loadImage(html.toDataURL());
    return out;
  }

  // Mask via alpha-compositing (destination-in). If `flip` is true, mask is flipped horizontally.
  async applyMaskToImage(image: HTMLImageElement, mask: HTMLImageElement, flip: boolean = true): Promise<string> {
    const w = image.width, h = image.height;
    const can = this.runtime.create(w, h);
    const ctx = this.runtime.get2D(can);

    // Draw the base image first
    ctx.drawImage(image, 0, 0, w, h);

    // Use mask as alpha via destination-in
    (ctx as any).globalCompositeOperation = 'destination-in';
    ctx.save();
    if (flip) {
      ctx.translate(w, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(mask, 0, 0, w, h);
    } else {
      ctx.drawImage(mask, 0, 0, w, h);
    }
    ctx.restore();

    // Return data URL
    if (this.runtime instanceof OffscreenCanvasRuntime) {
      return await (this.runtime as OffscreenCanvasRuntime).toDataURL(can as OffscreenCanvas);
    }
    // Fallback (shouldn't happen in this subclass)
    const html = this.runtime.toHTMLCanvas(can);
    return html.toDataURL();
  }

async hasTransparency(
  source: ImageSource,
  opts?: { threshold?: number; sample?: number } // sample=1 checks every pixel; higher = faster but approximate
): Promise<boolean> {
  const threshold = this.clamp(opts?.threshold ?? 255, 0, 255);
  const sample = Math.max(1, Math.floor(opts?.sample ?? 1));
  const blob = await this.toBlob(source);

  // --- Path 1: WebCodecs (no canvas; widely supported in Chromium/Firefox workers) ---
  if (typeof (globalThis as any).ImageDecoder !== "undefined") {
    try {
      const decoder = new (globalThis as any).ImageDecoder({ data: blob, type: blob.type || "image/png" });
      const { image } = await decoder.decode({ frameIndex: 0, completeFramesOnly: true }); // image is a VideoFrame
      const w = image.codedWidth;
      const h = image.codedHeight;

      // RGBA buffer big enough for the whole frame
      const buf = new Uint8Array(image.allocationSize({ format: "RGBA" }));
      await image.copyTo(buf, {
        format: "RGBA",
        layout: [{ offset: 0, stride: w * 4 }],
      });

      image.close();
      decoder.close();

      for (let i = 3; i < buf.length; i += 4 * sample) {
        if (buf[i] < threshold) return true;
      }
      return false;
    } catch {
      // fall through to OffscreenCanvas path if decoding failed
    }
  }

  // --- Path 2: OffscreenCanvas + createImageBitmap ---
  if (
    typeof (globalThis as any).OffscreenCanvas === "function" &&
    typeof (globalThis as any).createImageBitmap === "function"
  ) {
    const bitmap = await createImageBitmap(blob, { colorSpaceConversion: "none", premultiplyAlpha: "none" });
    const w = bitmap.width;
    const h = bitmap.height;

    const canvas = new OffscreenCanvas(w, h);
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) {
      bitmap.close();
      throw new Error("2D context not available in this worker.");
    }

    // @ts-ignore
    ctx.drawImage(bitmap, 0, 0);
    bitmap.close();

    // Read in vertical bands to keep memory bounded for very large images
    const bandH = Math.min(256, h);
    for (let y = 0; y < h; y += bandH) {
      const ph = Math.min(bandH, h - y);
      // @ts-ignore
      const img = ctx.getImageData(0, y, w, ph);
      const data = img.data; // RGBA
      for (let i = 3; i < data.length; i += 4 * sample) {
        if (data[i] < threshold) return true;
      }
    }
    return false;
  }

    // If neither path is available, be explicit.
    throw new Error(
      "No supported image decode path in this Service Worker. Need WebCodecs ImageDecoder or OffscreenCanvas + createImageBitmap."
    );
  }

  // ---------- Helpers ----------
  async toBlob(source: ImageSource): Promise<Blob> {
    if (source instanceof Blob) return source;
    if (source instanceof Response) return await source.blob();
    if (source instanceof ArrayBuffer) return new Blob([source]);
    if (typeof source === "string" || source instanceof URL) {
      const res = await fetch(source.toString(), { mode: "cors" });
      return await res.blob();
    }
    throw new TypeError("Unsupported image source");
  }

  clamp(n: number, min: number, max: number) {
    return Math.min(max, Math.max(min, n));
  }

}